/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AiToAiCallResponse } from '../models/AiToAiCallResponse';
import type { AmdCallbackRequest } from '../models/AmdCallbackRequest';
import type { Call } from '../models/Call';
import type { CallDaten } from '../models/CallDaten';
import type { CallStatusInboundRequest } from '../models/CallStatusInboundRequest';
import type { CallStatusRequest } from '../models/CallStatusRequest';
import type { OnboardingCallDaten } from '../models/OnboardingCallDaten';
import type { RecordingCallbackRequest } from '../models/RecordingCallbackRequest';
import type { TestCallDaten } from '../models/TestCallDaten';
import type { TestCallDatenAiToAi } from '../models/TestCallDatenAiToAi';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class CallsService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Es wird ein Twilio Call gestartet und ein Ultravox Call
   * Erstellt einen neuen ausgehenden Anruf
   * @returns Call Der erstellte Call
   * @throws ApiError
   */
  public createCall({
    requestBody,
  }: {
    /**
     * Daten, mit welchen der Call erstellt werden soll
     */
    requestBody: CallDaten,
  }): CancelablePromise<Call> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (createCall)`,
        403: `Forbidden for this request (createCall)`,
      },
    });
  }

  /**
   * starte Test Anruf
   * Starte einen Test Call mit der angegebenen Telefonnummer
   * @returns void
   * @throws ApiError
   */
  public starteTestCall({
    requestBody,
  }: {
    /**
     * Daten für den Test Call
     */
    requestBody: TestCallDaten,
  }): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/testanruf',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request`,
        403: `Forbidden for this request (starteTestCall)`,
        404: `Something is Not Found`,
      },
    });
  }

  /**
   * AI zu AI Call starten
   * Startet AI zu AI Calls mit zusätzlichem Speech Model Prompt
   * @returns AiToAiCallResponse AI zu AI Call erfolgreich erstellt
   * @throws ApiError
   */
  public aiToAiCall({
    requestBody,
  }: {
    /**
     * Daten für AI zu AI Calls mit zusätzlichem Speech Model Prompt
     */
    requestBody: TestCallDatenAiToAi,
  }): CancelablePromise<AiToAiCallResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/ai_to_ai',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (aiToAiCall)`,
        403: `Forbidden for this request (aiToAiCall)`,
        404: `Something is Not Found`,
      },
    });
  }

  /**
   * starte Onboarding Anruf
   * Starte einen Onboarding Call mit der angegebenen Telefonnummer
   * @returns void
   * @throws ApiError
   */
  public starteOnboardingCall({
    requestBody,
  }: {
    requestBody: OnboardingCallDaten,
  }): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/onboardinganruf',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (starteOnboardingCall)`,
        403: `Forbidden for this request (starteOnboardingCall)`,
        404: `Something is Not Found for this request (starteOnboardingCall)`,
      },
    });
  }

  /**
   * Informantionen zum Call werden entgegen genommen
   * Callback Url for Twilio to Report Call status
   * @returns any Answer from Twilio accepted
   * @throws ApiError
   */
  public callCallback({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Eindeutige ID der entsprechenden Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData: CallStatusRequest,
  }): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/callback/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
    });
  }

  /**
   * Informationen zum Call-Recording werden entgegen genommen
   * Callback Url for Twilio to Report Recording status
   * @returns any Answer from Twilio is accepted
   * @throws ApiError
   */
  public callRecordingCallback({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Eindeutige ID von der entsprechenden Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData: RecordingCallbackRequest,
  }): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/callback/recording/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
    });
  }

  /**
   * Informationen zum eingehenden Call werden entgegen genommen
   * Callback Url for Twilio to Report Inbound Call status
   * @returns string Answer from Twilio is accepted
   * @throws ApiError
   */
  public callCallbackInbound({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Eindeutige ID der entsprechenden Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData: CallStatusInboundRequest,
  }): CancelablePromise<string> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/callback/inbound/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
    });
  }

  /**
   * Informationen zum Call-Recording für inbound werden entgegen genommen
   * Callback Url for Twilio to Report Recording status from inbound call
   * @returns string Answer from Twilio is accepted
   * @throws ApiError
   */
  public callRecordingCallbackInbound({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Eindeutige ID von der entsprechenden Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData: RecordingCallbackRequest,
  }): CancelablePromise<string> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/callback/recording/inbound/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
    });
  }

  /**
   * Informationen, ob der Anrufbeantworter rangegangen ist
   * Callback Url for Twilio to Report AMD status
   * @returns any Answer from Twilio is accepted
   * @throws ApiError
   */
  public callamdCallback({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Eindeutige ID von der entsprechenden Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData: AmdCallbackRequest,
  }): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/callback/amd/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
    });
  }

  /**
   * IVR Consent Webhook - Initiiert Bandansagen Flow
   * Twilio webhook Endpunkt, welcher den IVR Bandansagen flow initiiert.
   * @returns string TwiML response with consent prompt
   * @throws ApiError
   */
  public ivrConsent({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Unique ID of the corresponding Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData?: {
      /**
       * Twilio Call SID - unique identifier for the call
       */
      CallSid?: string;
    },
  }): CancelablePromise<string> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/ivr/ivr-consent',
      query: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
      errors: {
        500: `Fehler ist aufgetreten`,
      },
    });
  }

  /**
   * Twilio webhook - Verbindet eingehenden Twilio Call mit Ultravox
   * Twilio webhook Endpunkt, der nach Verbindungsaufbau des ausgehenden Twilio Calls
   * den Ultravox Call erstellt (mit bekanntem Twilio CallSid) und TwiML zum
   * Verbinden mit dem Ultravox Stream zurückgibt.
   *
   * @returns string TwiML response welche den Twilio Call mit Ultravox verbindet
   * @throws ApiError
   */
  public ultravoxConnect({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Unique ID of the corresponding Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData?: {
      /**
       * Twilio Call SID - unique identifier for the call
       */
      CallSid?: string;
    },
  }): CancelablePromise<string> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/ultravox-connect/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
      errors: {
        500: `Fehler ist aufgetreten`,
      },
    });
  }

  /**
   * IVR Consent Response Webhook - Processes user Bandansagen Entscheidung
   * Twilio webhook Endpunkt welcher die Nutzerantwort verarbeitet und die nötigen Schritte startet.
   * @returns string TwiML antwort, basierend auf Nutzerentscheidung
   * @throws ApiError
   */
  public consentResponse({
    kontaktaufnahmeId,
    formData,
  }: {
    /**
     * Kontaktaufnahmen Id der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    formData: {
      /**
       * Gedrückte Taste (1 = Zustimmung)
       */
      Digits?: string;
      /**
       * Twilio Call SID
       */
      CallSid?: string;
    },
  }): CancelablePromise<string> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/calls/ivr/consent-response',
      query: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      formData: formData,
      mediaType: 'application/x-www-form-urlencoded',
      errors: {
        500: `Fehler aufgetreten - TwiML mit Error Nachricht`,
      },
    });
  }

}
